import React, {useEffect, useMemo, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from "react-intl";
import {Box, Stack} from "@mui/material";

import CustomButton from "components/CustomButton";
import ArrowRightIcon from "components/icons/ArrowRightIcon";
import {getTranslation} from "domain/helpers/translations";
import {ReactComponent as CloseIcon} from "assets/img/close.svg";
import {red600} from "assets/jss/main";

import {fieldFor} from "../attributeField";
import {ENRICHMENT_STATE_FIELDS, ENRICHMENT_TABS, targetComparator, targetLabel} from "../enrichmentConstants";

import AttributesDialog from "./AttributesDialog";
import DecisionAuthor from "./DecisionAuthor";
import {Cell, Row} from "./EnrichmentTable";
import ProductCell from "./ProductCell";
import ScoringCell from "./ScoringCell";
import TargetEditor from "./TargetEditor";
import VariantChips from "./VariantChips";

AttributeRow.propTypes = {
    tab: PropTypes.oneOf(Object.keys(ENRICHMENT_STATE_FIELDS)).isRequired,
    product: PropTypes.object.isRequired,
    state: PropTypes.oneOf(['missing', 'pending', 'applied']).isRequired,
    values: PropTypes.object.isRequired,
    selected: PropTypes.bool.isRequired,
    onSelect: PropTypes.func.isRequired,
    onApply: PropTypes.func.isRequired,
    onApplyValues: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onChoose: PropTypes.func.isRequired,
    onChangeApplied: PropTypes.func.isRequired,
    onValuesChange: PropTypes.func.isRequired,
    deciding: PropTypes.bool,
};

const MAX_INLINE_TARGETS = 2;

const squareButtonSx = {minWidth: '40px', width: '40px', height: '40px', padding: 0, '& svg': {width: '16px', height: '16px'}, '& svg path': {fill: red600}};

export function suggestionsByTarget(product, status) {
    return Object.fromEntries((product.enrichmentSuggestions ?? [])
        .filter((suggestion) => (suggestion.status ?? 'pending') === status)
        .map((suggestion) => [suggestion.target, suggestion]));
}

export function suggestedValues(product) {
    return Object.fromEntries(Object.entries(suggestionsByTarget(product, 'pending')).map(([target, suggestion]) => [target, suggestion.value]));
}

function AttributeRow({tab, product, state, values: catalog, selected, onSelect, onApply, onApplyValues, onDismiss, onChoose, onChangeApplied, onValuesChange, deciding = false}) {
    const pendingSuggestions = useMemo(() => suggestionsByTarget(product, 'pending'), [product]);
    const appliedSuggestions = useMemo(() => suggestionsByTarget(product, 'applied'), [product]);
    const [values, setValues] = useState(() => suggestedValues(product));
    const [dialogOpen, setDialogOpen] = useState(false);
    const [resets, setResets] = useState({});
    const intl = useIntl();

    useEffect(() => {
        onValuesChange(product.id, values);
    }, [product.id, values, onValuesChange]);

    const targets = product[ENRICHMENT_STATE_FIELDS[tab][state]] ?? [];
    const sortedTargets = [...targets].sort(targetComparator(catalog));
    const grouped = ENRICHMENT_TABS.SECONDARY_ATTRIBUTES === tab && (dialogOpen || sortedTargets.length > MAX_INLINE_TARGETS);
    const valueOf = (target) => ('applied' === state ? appliedSuggestions[target]?.value : values[target]);
    const aiValueOf = (target) => {
        const suggestion = ('applied' === state ? appliedSuggestions : pendingSuggestions)[target];

        return suggestion && 'manual' !== suggestion.source ? suggestion.value : null;
    };
    const lastApplied = Object.values(appliedSuggestions).sort((a, b) => (b.decidedAt ?? '').localeCompare(a.decidedAt ?? ''))[0];

    const editors = (
        <Stack gap="12px">
            {sortedTargets.map((target) => (
                <Stack key={target} direction="row" alignItems="center" gap="12px">
                    <TargetEditor
                        key={resets[target] ?? 0}
                        target={target}
                        field={fieldFor(catalog, target)}
                        value={valueOf(target)}
                        suggestedValue={aiValueOf(target)}
                        highlighted={'missing' === state}
                        disabled={deciding}
                        onChange={(value) => {
                            if ('missing' === state) {
                                onChoose(product, target, value);
                            } else if ('applied' === state) {
                                onChangeApplied(product, target, value).then((saved) => {
                                    if (!saved) {
                                        setResets((current) => ({...current, [target]: (current[target] ?? 0) + 1}));
                                    }
                                });
                            } else {
                                setValues((current) => ({...current, [target]: value}));
                            }
                        }}
                    />
                    {'missing' === state && (
                        <CustomButton variant="contained" color="lightRed" sx={squareButtonSx} aria-label={intl.formatMessage({id: 'enrichment.action.dismissTarget'}, {target: targetLabel(intl, catalog, target)})} onClick={() => onDismiss(product, [target])}>
                            <CloseIcon/>
                        </CustomButton>
                    )}
                </Stack>
            ))}
        </Stack>
    );

    const groupedEditors = (
        <Stack direction="row" alignItems="center" gap="12px">
            <CustomButton variant="contained" color="default" fullWidth onClick={() => setDialogOpen(true)} sx={{justifyContent: 'space-between'}}>
                <FormattedMessage id="enrichment.attributes.seeAll" values={{count: sortedTargets.length}}/>
                <ArrowRightIcon size={16}/>
            </CustomButton>
            {'missing' === state && (
                <CustomButton variant="contained" color="lightRed" sx={squareButtonSx} aria-label={intl.formatMessage({id: 'enrichment.action.dismissSelection'})} onClick={() => onDismiss(product, targets)}>
                    <CloseIcon/>
                </CustomButton>
            )}
            {dialogOpen && (
                <AttributesDialog
                    open
                    onClose={() => setDialogOpen(false)}
                    state={state}
                    targets={sortedTargets}
                    catalog={catalog}
                    initialValues={Object.fromEntries(sortedTargets.map((target) => [target, valueOf(target)]))}
                    suggestedValues={Object.fromEntries(sortedTargets.map((target) => [target, aiValueOf(target)]))}
                    deciding={deciding}
                    onApply={(dialogValues) => onApplyValues(product, dialogValues)}
                    onDismiss={(dismissed) => onDismiss(product, dismissed)}
                    onChangeApplied={(target, value) => onChangeApplied(product, target, value)}
                />
            )}
        </Stack>
    );

    return (
        <Row>
            <Cell column="product">
                <ProductCell
                    name={getTranslation(product).name}
                    retailerName={product.retailerName}
                    brandName={product.brand?.name}
                    reference={product.sku}
                    pictureUrl={product.pictures[0] ?? null}
                    groupedSearchImpression={product.groupedSearchImpression}
                    ctr={product.ctr}
                    selectable={'applied' !== state}
                    selected={selected}
                    onSelect={() => onSelect(product, !selected)}
                />
            </Cell>
            <Cell column="scoring">
                <ScoringCell completeness={product.validationScore} score={product.score} potentialScore={product.potentialScore}/>
            </Cell>
            <Cell column="variants">
                <VariantChips labels={(product.variations ?? []).map((variation) => variation.name)}/>
            </Cell>
            <Cell column={'pending' === state ? 'suggestion' : 'attributes'}>
                {grouped ? groupedEditors : editors}
            </Cell>
            {'pending' === state && (
                <Cell column="action">
                    <Stack direction="row" gap="8px">
                        <Box sx={{flex: 1}}>
                            <CustomButton variant="contained" color="green" fullWidth loading={deciding} onClick={() => onApply(product, values)}>
                                <FormattedMessage id="enrichment.action.apply"/>
                            </CustomButton>
                        </Box>
                        <CustomButton variant="contained" color="lightRed" sx={squareButtonSx} aria-label={intl.formatMessage({id: 'enrichment.action.dismiss'})} onClick={() => onDismiss(product, targets)}>
                            <CloseIcon/>
                        </CustomButton>
                    </Stack>
                </Cell>
            )}
            {'applied' === state && (
                <Cell column="action">
                    <DecisionAuthor name={lastApplied?.decidedBy ?? null} decidedAt={lastApplied?.decidedAt ?? null}/>
                </Cell>
            )}
        </Row>
    );
}

export default AttributeRow;
